import { useEffect, useRef, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  apiClient,
  clearTokenGetter,
  setTokenGetter,
  setAuthErrorHandler,
  clearAuthErrorHandler,
} from "@/services/api";

/**
 * useApi Hook
 *
 * Custom hook that provides access to the API client with automatic token injection.
 * Initializes the API client with Auth0 token getter SYNCHRONOUSLY on first render
 * to avoid race conditions where API calls are made before the token getter is set.
 *
 * Usage:
 * ```typescript
 * import { useApi } from '@/hooks/useApi';
 *
 * function MyComponent() {
 *   const api = useApi();
 *
 *   useEffect(() => {
 *     const fetchData = async () => {
 *       const data = await api.get('/api/v1/endpoint');
 *       console.log(data);
 *     };
 *     fetchData();
 *   }, [api]);
 * }
 * ```
 */
export function useApi() {
  const { getAccessTokenSilently, isAuthenticated, logout } = useAuth0();
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;
  const isInitialized = useRef(false);

  // Memoized logout handler to prevent unnecessary re-renders
  const handleAuthError = useCallback(() => {
    console.log("Auth error detected, logging out user...");
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  if (isAuthenticated && !isInitialized.current) {
    const tokenGetter = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience,
          },
          cacheMode: "on",
        });
        return token;
      } catch (error) {
        console.error("Error getting access token:", error);
        throw error;
      }
    };
    setTokenGetter(tokenGetter);
    setAuthErrorHandler(handleAuthError);
    isInitialized.current = true;
  }

  // Handle cleanup and re-initialization when auth state changes
  useEffect(() => {
    if (!isAuthenticated) {
      clearTokenGetter();
      clearAuthErrorHandler();
      isInitialized.current = false;
      return;
    }

    // Re-initialize if auth state changed (e.g., different user logged in)
    const tokenGetter = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience,
          },
          cacheMode: "on",
        });
        return token;
      } catch (error) {
        console.error("Error getting access token:", error);
        throw error;
      }
    };
    setTokenGetter(tokenGetter);
    setAuthErrorHandler(handleAuthError);

    return () => {
      clearTokenGetter();
      clearAuthErrorHandler();
      isInitialized.current = false;
    };
  }, [audience, getAccessTokenSilently, isAuthenticated, handleAuthError]);

  return apiClient;
}
